import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { isAfter, subDays } from "date-fns";

import {
  formatDate,
  formatDateAt,
  formatDateRelative,
} from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import { DownloadLink } from "simplydo/react/components/DownloadLink";
import { styled } from "simplydo/react/styled";
import { Icon, Loader, Menu, Message, Popup } from "simplydo/react/ui";
import type { SemanticICONS } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { EmptyBox } from "../UI";

const StyledDownloadContainer = styled.div<{ $disabled?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 5px;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  ${(props) =>
    !props.$disabled &&
    `
    &:hover {
      background-color: rgb(233, 235, 238);
      border-radius: 5px;
    }
  `}
`;

const DownloadsContainer = styled.div`
  max-height: 500px;
  overflow: auto;
  padding-right: 5;
`;

type DownloadsProps = { downloadCompleted: boolean; page: number };

export const FileIconMap = {
  pdf: "file pdf outline",
  doc: "file word outline",
  docx: "file word outline",
  xls: "file excel outline",
  xlsx: "file excel outline",
  ppt: "file powerpoint outline",
  pptx: "file powerpoint outline",
  jpg: "file image outline",
  jpeg: "file image outline",
  png: "file image outline",
  gif: "file image outline",
  mp4: "file video outline",
  mp3: "file audio outline",
  zip: "file archive outline",
  rar: "file archive outline",
  txt: "file text outline",
  other: "file outline",
  csv: "file alternate outline",
} as const;

type FileIconKey = keyof typeof FileIconMap;

const getFileIcon = (ext: string | undefined): SemanticICONS => {
  if (ext && ext in FileIconMap) {
    return FileIconMap[ext as FileIconKey] as SemanticICONS;
  }
  return FileIconMap.other;
};

const downloadTypeMap = {
  ideaPdfExport: "$t(generic.idea,capitalise) PDF document",
  ideaDocxExport: "$t(generic.idea,capitalise) Word document",
  challengePdfExport: "Engagement report",
  assessmentXlsxExport: "Assessment report",
  userAssessmentXlsxExport: "Personal assessment report",
  pollPdfExport: "Poll report",
  pollCsvExport: "Poll results",
  groupBusinessCsvExport: "Group business list",
  groupUserCsvExport: "Group user list",
  challengeXlsxExport:
    "$t(generic.challenge,capitalise) $t(generic.idea) report",
} as const;

type DownloadTypeKey = keyof typeof downloadTypeMap;

const getDownloadTypeLabel = (downloadType: string): string => {
  if (downloadType in downloadTypeMap) {
    return downloadTypeMap[downloadType as DownloadTypeKey];
  }
  return downloadType;
};

const Downloads = ({ downloadCompleted, page }: DownloadsProps) => {
  const user = useAppSelector((state) => state.user);
  const userId = user?._id;
  const { t } = useTranslation();

  const downloadsQuery = useQuery(
    initUseQuery(api.users.getDownloads, [userId ?? "", { limit: 10, page }], {
      isEnabled: !!userId,
      refetchInterval: ({ state }) => {
        if ((state.data?.pendingDownloads?.length ?? 0) > 0) {
          return 5000;
        }
        return false;
      },
    }),
  );

  const { downloads = [], pendingDownloads = [] } = downloadsQuery?.data ?? {};
  if (downloads.length === 0 && pendingDownloads.length === 0) {
    return <EmptyBox message="You have not created any downloads" />;
  }
  return (
    <div>
      <DownloadsContainer>
        {pendingDownloads.length > 0 && (
          <>
            {!downloadCompleted && (
              <Message
                info
                content="Your download has started, we will notify you once it is completed."
              />
            )}
            <h5>Pending downloads</h5>
            {pendingDownloads.map((download) => (
              <Menu.Item
                key={download._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  textOverflow: "ellipsis",
                }}
                fitted
                link
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "98%",
                  }}
                >
                  <p>
                    <Icon name="file pdf outline" style={{ marginRight: 5 }} />
                    {download.context.displayName}
                  </p>
                  <Loader active inline size="tiny" />
                </div>
              </Menu.Item>
            ))}
          </>
        )}
        {downloads.length > 0 && (
          <>
            {downloadCompleted && (
              <Message
                info
                succes
                content="Your download has finished, you can download it by clicking below."
              />
            )}
            <h5>Completed downloads</h5>
            {downloads.map((download) => {
              const displayName = download.context.displayName;
              const ext = displayName.split(".").pop();
              const icon = getFileIcon(ext);

              return (
                <Popup
                  key={download._id}
                  disabled={download.status === "success"}
                  trigger={
                    <DownloadLink
                      className="item"
                      url={download.url ? download.url : ""}
                      name={download.context.displayName}
                      style={{
                        display: "flex",
                        textOverflow: "ellipsis",
                        marginBottom: 5,
                        color: "black",
                        cursor:
                          download.status !== "failed" &&
                          download.status !== "expired" &&
                          "pointer",
                        paddingBottom: 5,
                        borderBottom: "1px solid rgba(34,36,38,.15)",
                        opacity:
                          download.status === "failed" ||
                          download.status === "expired"
                            ? 0.6
                            : 1,
                      }}
                    >
                      <StyledDownloadContainer
                        $disabled={download.status !== "success"}
                        onClick={(e) => {
                          if (download.status !== "success") {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Icon
                            name={icon}
                            size="big"
                            style={{ marginRight: 12 }}
                          />
                          <div style={{ maxWidth: 420 }}>
                            <p
                              style={{
                                marginBottom: 2,
                                maxWidth: "100%",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {download.context.displayName}
                            </p>
                            <p
                              style={{
                                color: "grey",
                                fontSize: 12,
                                lineHeight: "10px",
                                marginBottom: 0,
                              }}
                            >
                              {t(
                                getDownloadTypeLabel(
                                  download.context.downloadType,
                                ),
                              )}
                            </p>
                            <span
                              style={{
                                color: "grey",
                                fontSize: 12,
                                lineHeight: "10px",
                              }}
                            >
                              {formatDateAt(download.createdAt)}
                              {download.status === "failed" ? (
                                <span
                                  style={{ color: "#e7381c", fontSize: 12 }}
                                >
                                  {" "}
                                  - Download failed
                                </span>
                              ) : null}
                              {download.status === "expired" ? (
                                <span style={{ fontSize: 12, marginTop: -2 }}>
                                  {" "}
                                  - Download expired
                                </span>
                              ) : null}
                            </span>
                          </div>
                        </div>
                        {download.status === "success" ? (
                          <Icon name="download" />
                        ) : null}
                      </StyledDownloadContainer>
                    </DownloadLink>
                  }
                  content={
                    <div style={{ maxWidth: 300 }}>
                      {download.status === "failed"
                        ? "Something went wrong when trying to generate this file. Please try again, or contact us directly if this issue persists."
                        : "This download link has expired, please generate the download again if needed."}
                    </div>
                  }
                />
              );
            })}
          </>
        )}
      </DownloadsContainer>
    </div>
  );
};

export default Downloads;
